import type {
  Campaign,
  PremiumTier,
  SuccessCriteria,
} from '../shared/content/campaigns';
import type { CartItem, CartItemDTO } from './cart-item';
import { ErrorCart } from './errors';

export interface CartPremium extends Omit<SuccessCriteria, 'premiumTiers'> {
  tiers?: PremiumTier[];
}

export interface CartSubsidy {
  subsidizedDuration: number | null;
  subsidizedCdspRate: number | null;
}
export interface CartUpsell {
  // TODO: Once we're not getting the limits from Tridion, we should allow the component to decide when to offer the upsell based on the counts
  canOffer?: boolean;
  // These are string arrays to allow not upselling the same item that they declined
  acceptedIds: string[];
  declinedIds: string[];
}

export enum CartUpsellActionEnum {
  IncrementAcceptCount = 'incrementAcceptCount',
  IncrementDeclineCount = 'incrementDeclineCount',
}

export enum CartUpsellIncrementType {
  Accept = 'accept',
  Decline = 'decline',
}

export enum CartDisclaimerEnum {
  None,
  Recurring,
  Subsidy,
}

export interface Cart {
  /**
   * Cart ID.
   */
  id: string;
  /**
   * Timestamp (ISO in ms) the cart was first created.
   * Always exists for carts that have been saved to db.
   *
   */
  created?: string;
  /**
   * Timestamp (ISO in ms) the cart was completed.
   * If this value exists, then the cart cannot be manipulated
   *
   */
  completed?: string;
  /**
   * Timestamp (ISO in ms) essentially the last-modified value when the cart was saved to the DB.
   * Always exists for carts that have been saved to db.
   */
  version: string;
  /**
   * The checkout URL to begin the checkout experience.
   */
  checkoutUrl?: string;
  /**
   * This is the source code that we expect to be the transaction source code. Used to help sourceCode visitors
   */
  sourceCode?: string;
  /**
   * A hash to be used to verify if a source code needs to be recalculated or not
   */
  sourceCodeHash?: string;
  /**
   * Campaign object from Tridion/Contentful
   */
  campaign?: Campaign;
  /**
   * TcmId of an active campaign
   */
  campaignId?: string;
  /**
   * Salesforce Account Id if the supporter is logged in
   */
  supporterId?: string;
  /**
   * Total (float) that will be pulled today.
   */
  totalToday: number;
  /**
   * Total (float) of all the item "amount" values for non-monthly frequency items and excluding any gifts that have "noPaymentToday" set to true.
   */
  totalOneTime: number;
  /**
   * Total (float) of all the item "unitPrice" values that aren't OneTime or Yearly
   */
  totalMonthly: number;
  /**
   * Total (float)of all the item "amount" values for annual items
   */
  totalYearly: number;
  /**
   * Cart items.
   */
  items: CartItem[];
  /**
   * Cart has campaign subsidy
   */
  subsidy?: CartSubsidy;
  /**
   * Present when a Premium is active for the cart
   */
  premium?: PremiumTier;
  /** NOTE: expecting to remove this work, based on another story regarding `premium`s */
  // export interface CartPremium extends Omit<SuccessCriteria, 'premiumTiers'> {
  // 	tiers?: Premium[],

  /**
   * Whether or not the cart meets the success criteria for the campaign
   */
  meetsCriteria?: boolean;

  /**
   * Information pertaining to an Upsell (e.g. accept/decline user interactions)
   */
  upsell?: CartUpsell;
  /**
   * Cart items that were removed.
   */
  removedItems?: CartItem[];
  /**
   * A collection of cart errors
   */
  // TODO: Should this just be simplified to ErrorBase (the simple interface which is flexible)?
  // Doesn't seem like we're using the individual error interfaces and that makes for more work with each error added
  errors?: ErrorCart[];
}

export type CartKeys = keyof Cart;
export type CartKeyTypes = Cart[CartKeys];

export interface CartResponseDTO extends Omit<Cart, 'items' | 'removedItems'> {
  items: CartItemDTO[];
  removedItems?: CartItemDTO[];
}
